import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Sheet,
  Stack,
  Typography,
} from "@mui/joy";
import { theme } from "styles/theme";
import type { AIGenerateStatus } from "./useTaskEditorAIGenerate";

interface TaskEditorAIStatusBannerProps {
  status: AIGenerateStatus;
  onApply: () => void;
  onPreview: () => void;
  onDismiss: () => void;
}

export default function TaskEditorAIStatusBanner({
  status,
  onApply,
  onPreview,
  onDismiss,
}: TaskEditorAIStatusBannerProps) {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (status.type !== "generating") {
      setElapsed(0);
      return;
    }

    const interval = setInterval(() => {
      setElapsed(Math.floor((Date.now() - status.startedAt) / 1000));
    }, 1000);

    return () => clearInterval(interval);
  }, [status]);

  if (status.type === "idle") {
    return null;
  }

  if (status.type === "generating") {
    return (
      <Sheet
        variant="soft"
        color="primary"
        sx={{
          p: "12px",
          borderRadius: "12px",
        }}
      >
        <Stack spacing={1}>
          <Stack direction="row" spacing={1} alignItems="center">
            <CircularProgress size="sm" color="primary" />
            <Typography
              variant="Caption"
              sx={{
                color: theme.vars.palette.Content.Strong,
                fontWeight: 700,
              }}
            >
              AI 초안 생성 중...
            </Typography>
          </Stack>
          <Typography
            variant="Caption"
            sx={{ color: theme.vars.palette.Content.Weak }}
          >
            {elapsed}초 경과
            {status.type === "generating" && status.progress != null
              ? ` (${Math.round(status.progress)}%)`
              : ""}
            {" — 다른 작업을 계속할 수 있습니다"}
          </Typography>
          <Button
            size="sm"
            variant="Plain"
            color="neutral"
            onClick={onDismiss}
            sx={{
              alignSelf: "flex-start",
              fontSize: "12px",
              height: "28px",
              px: "8px",
            }}
          >
            취소
          </Button>
        </Stack>
      </Sheet>
    );
  }

  if (status.type === "completed") {
    return (
      <Sheet
        variant="soft"
        color="success"
        sx={{
          p: "12px",
          borderRadius: "12px",
        }}
      >
        <Stack spacing={1.5}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box
              sx={{
                width: "20px",
                height: "20px",
                borderRadius: "999px",
                backgroundColor: "success.500",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <span
                className="material-symbols-rounded"
                style={{ fontSize: "14px", color: "#fff" }}
              >
                check
              </span>
            </Box>
            <Typography
              variant="Caption"
              sx={{
                fontWeight: 700,
                color: theme.vars.palette.Content.Strong,
              }}
            >
              AI 초안이 완성되었습니다
            </Typography>
          </Stack>
          <Typography
            variant="Caption"
            sx={{ color: theme.vars.palette.Content.Weak }}
          >
            {status.result.title || "새 활동지"} — 블록{" "}
            {status.result.blocks.filter((b) => b.type !== "LastCard").length}개
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button
              size="sm"
              variant="Outlined"
              color="neutral"
              onClick={onPreview}
              sx={{ fontSize: "12px", height: "32px", px: "12px" }}
            >
              미리보기
            </Button>
            <Button
              size="sm"
              variant="Solid"
              onClick={onApply}
              sx={{ fontSize: "12px", height: "32px", px: "12px" }}
            >
              에디터에 적용
            </Button>
            <Button
              size="sm"
              variant="Plain"
              color="neutral"
              onClick={onDismiss}
              sx={{ fontSize: "12px", height: "32px", px: "8px" }}
            >
              닫기
            </Button>
          </Stack>
        </Stack>
      </Sheet>
    );
  }

  // error
  return (
    <Sheet
      variant="soft"
      color="danger"
      sx={{
        p: "12px",
        borderRadius: "12px",
      }}
    >
      <Stack spacing={1}>
        <Typography
          variant="Caption"
          sx={{
            fontWeight: 700,
            color: theme.vars.palette.Content.Strong,
          }}
        >
          AI 생성 실패
        </Typography>
        <Typography
          variant="Caption"
          sx={{ color: theme.vars.palette.Content.Weak }}
        >
          {status.message}
        </Typography>
        <Button
          size="sm"
          variant="Plain"
          color="neutral"
          onClick={onDismiss}
          sx={{
            alignSelf: "flex-start",
            fontSize: "12px",
            height: "28px",
            px: "8px",
          }}
        >
          닫기
        </Button>
      </Stack>
    </Sheet>
  );
}
